import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle2, ArrowRight, Phone, Mail, Calculator, BookOpen, DollarSign, Pill, UtensilsCrossed, Wrench, Dumbbell } from 'lucide-react';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import { trackForecastCtaClicked, trackLeadSubmitted, setFunnelPosition } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const navigate = useNavigate();
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    primaryGoal: '',
    interestedIn: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const scrollToForm = () => {
    document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneDigits = phone.replace(/\D/g, '');
    return phoneDigits.length === 10;
  };

  const normalizePhoneNumber = (phone) => {
    // Normalize to (XXX) XXX-XXXX format for storage
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return phone;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData({ ...formData, phone: formatted });
      
      // Only show error if they've typed enough digits
      const phoneDigits = value.replace(/\D/g, '');
      if (phoneDigits.length > 0 && phoneDigits.length < 10) {
        setPhoneError('Phone number must be 10 digits');
      } else {
        setPhoneError('');
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setError('');
    setPhoneError('');
    
    // Validate phone number before submission
    if (!validatePhoneNumber(formData.phone)) {
      setPhoneError('Please enter a valid 10-digit US phone number');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Normalize phone number for storage
      const submissionData = {
        ...formData,
        phone: normalizePhoneNumber(formData.phone)
      };

      const response = await axios.post(`${API}/leads`, submissionData);
      
      if (response.status === 201) {
        // Track lead submission as conversion event
        trackLeadSubmitted({
          primaryGoal: formData.primaryGoal,
          interestedIn: formData.interestedIn
        });
        setFunnelPosition(4);
        
        // Successfully created lead, navigate to thank you page
        navigate('/thank-you');
      }
    } catch (err) {
      console.error('Error submitting lead:', err);
      const errorMessage = err.response?.data?.detail || 'Something went wrong. Please try again or call us directly at 801-651-8363';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]">
      <SEOHead 
        title="Medical Weight Loss with GLP-1 Medications"
        description="Work with Bryan Maldonado for personalized medical weight loss using semaglutide and tirzepatide. Real results, transparent pricing, and expert guidance."
        canonicalPath="/"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1020]/95 backdrop-blur-sm border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-xl font-bold text-[#F8FAFC]">Bryan Maldonado</Link>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="hidden md:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <BookOpen size={18} />
                <span>Blog</span>
              </Link>
              <div className="hidden md:block relative">
                <button 
                  onClick={() => setShowToolsDropdown(!showToolsDropdown)}
                  onBlur={() => setTimeout(() => setShowToolsDropdown(false), 200)}
                  className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors"
                >
                  <Wrench size={18} />
                  <span>Tools</span>
                </button>
                {showToolsDropdown && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-[#1E293B] border border-[#334155] rounded-lg shadow-xl z-50 overflow-hidden">
                    <button
                      onClick={() => { navigate('/cost-calculator'); setShowToolsDropdown(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left text-[#F8FAFC] hover:bg-[#334155] transition-colors"
                    >
                      <DollarSign size={18} className="text-[#22C55E]" />
                      <span>Cost Calculator</span>
                    </button>
                    <button
                      onClick={() => { navigate('/medication-match'); setShowToolsDropdown(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left text-[#F8FAFC] hover:bg-[#334155] transition-colors"
                    >
                      <Pill size={18} className="text-[#3B82F6]" />
                      <span>Medication Match</span>
                    </button>
                    <button
                      onClick={() => { navigate('/meal-plan'); setShowToolsDropdown(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left text-[#F8FAFC] hover:bg-[#334155] transition-colors"
                    >
                      <UtensilsCrossed size={18} className="text-[#F59E0B]" />
                      <span>Meal Planner</span>
                    </button>
                    <button
                      onClick={() => { navigate('/strength-plan'); setShowToolsDropdown(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left text-[#F8FAFC] hover:bg-[#334155] transition-colors"
                    >
                      <Dumbbell size={18} className="text-[#A855F7]" />
                      <span>Strength Plan</span>
                    </button>
                  </div>
                )}
              </div>
              <Link to="/start-here" className="hidden md:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <span>Start Here</span>
              </Link>
              <a href="tel:801-651-8363" className="hidden sm:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <Phone size={18} />
                <span>801-651-8363</span>
              </a>
              <Button onClick={() => {
                trackForecastCtaClicked('home_header');
                navigate('/calculator');
              }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                90-Day Forecast
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-full mb-6">
            <p className="text-[#3B82F6] text-sm font-semibold">No pressure. No quiz. Just real answers.</p>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight">
            If You've Already Been Contacted a Dozen Times, This Is Different
          </h1>
          <p className="text-xl sm:text-2xl text-[#94A3B8] mb-8 max-w-3xl mx-auto">
            We put together honest, straightforward information on GLP-1 medications — what they actually do, what results look like, and whether they make sense for you. No form required to read.
          </p>
          <div className="flex flex-col gap-4 max-w-md mx-auto mb-6">
            <Button 
              onClick={() => {
                trackForecastCtaClicked('home_hero');
                navigate('/calculator');
              }}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white text-lg px-8 py-6 h-auto"
              data-testid="hero-forecast-btn"
            >
              <Calculator className="mr-2" size={20} />
              Build My 90-Day Forecast
            </Button>
            <Button 
              onClick={scrollToForm}
              variant="outline"
              className="w-full border-[#1E293B] bg-transparent hover:bg-[#1E293B] text-[#F8FAFC] text-lg px-8 py-6 h-auto"
              data-testid="hero-consultation-btn"
            >
              Get Your Free Consultation
            </Button>
          </div>
          <p className="text-sm text-[#94A3B8] flex items-center justify-center gap-2 flex-wrap">
            <CheckCircle2 size={16} className="text-[#22C55E]" />
            <span>Professional guidance</span>
            <span className="text-[#1E293B]">•</span>
            <CheckCircle2 size={16} className="text-[#22C55E]" />
            <span>Evidence-based options</span>
            <span className="text-[#1E293B]">•</span>
            <CheckCircle2 size={16} className="text-[#22C55E]" />
            <span>No pressure approach</span>
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              Why Consider Medically Supported Weight Management?
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Professional oversight can provide additional support when traditional approaches have not delivered the results you are looking for.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-[#121A2B] border-[#1E293B] p-6">
              <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="text-[#3B82F6]" size={24} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">Professional Oversight</h3>
              <p className="text-[#94A3B8]">
                Work with licensed professionals who understand the science of weight management and can provide personalized recommendations.
              </p>
            </Card>
            <Card className="bg-[#121A2B] border-[#1E293B] p-6">
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="text-[#22C55E]" size={24} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">Prescription-Based Protocols</h3>
              <p className="text-[#94A3B8]">
                Medically supervised treatment using prescription medications, designed to support your wellness goals safely and effectively.
              </p>
            </Card>
            <Card className="bg-[#121A2B] border-[#1E293B] p-6">
              <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="text-[#3B82F6]" size={24} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">Ongoing Support</h3>
              <p className="text-[#94A3B8]">
                Continuous guidance and adjustments to help you stay on track, answer questions, and optimize your approach over time.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] text-center mb-4">
            Is Medical Weight Loss Right for You?
          </h2>
          <p className="text-[#94A3B8] text-center mb-12 max-w-2xl mx-auto">
            You might be a good candidate if you identify with any of these situations:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {[
              'You have tried traditional diets without lasting results',
              'You have lost weight before but gained it back',
              'You experience low energy or brain fog',
              'You want professional guidance and accountability',
              'You are looking for a science-backed approach',
              'You need a sustainable long-term solution'
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-[#0B1020] rounded-lg border border-[#1E293B] hover:border-[#3B82F6] transition-all duration-300">
                <CheckCircle2 className="text-[#22C55E] mt-1 flex-shrink-0" size={24} />
                <span className="text-[#F8FAFC] text-lg">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToForm}
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg px-8 py-6 h-auto"
              >
                Find Out If You Qualify
              </Button>
              <Button 
                onClick={() => navigate('/calculator')}
                variant="outline"
                className="border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] text-lg px-8 py-6 h-auto"
              >
                <Calculator className="mr-2" size={20} />
                See Your Forecast
              </Button>
            </div>
            <p className="text-sm text-[#94A3B8] mt-4">
              Free consultation • No obligation • Fast response
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              How It Works
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Getting started is simple. Here's what to expect when you work with us.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                num: '1', 
                title: 'Share Your Goals', 
                desc: 'Tell us about your health objectives, what you have tried before, and what you hope to achieve. This helps us understand your unique situation.' 
              },
              { 
                num: '2', 
                title: 'Get Personalized Guidance', 
                desc: 'Receive clear information about which options may work best for you, how they function, and what results you might expect.' 
              },
              { 
                num: '3', 
                title: 'Start with Support', 
                desc: 'Move forward with professional oversight, ongoing check-ins, and the ability to adjust your approach as needed.' 
              }
            ].map((step) => (
              <Card key={step.num} className="bg-[#121A2B] border-[#1E293B] p-6 text-center hover:border-[#3B82F6] transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">{step.title}</h3>
                <p className="text-[#94A3B8]">{step.desc}</p>
              </Card>
            ))}
          </div>
          <div className="mt-12 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToForm}
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg px-8 py-6 h-auto"
              >
                Get Started Today
              </Button>
              <Button 
                onClick={() => {
                  trackForecastCtaClicked('home_cta_section');
                  navigate('/calculator');
                }}
                variant="outline"
                className="border-[#3B82F6] bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 text-[#3B82F6] text-lg px-8 py-6 h-auto"
              >
                <Calculator className="mr-2" size={20} />
                Build My Forecast
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Free Tools Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0B1020] to-[#121A2B]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-full mb-4">
              <Wrench className="w-4 h-4 text-[#3B82F6]" />
              <p className="text-[#3B82F6] text-sm font-semibold">Free Tools</p>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              Make Informed Decisions
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Use our free calculators and tools to understand your options before you even talk to us.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Cost Calculator Card */}
            <Card 
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#22C55E] transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/cost-calculator')}
              data-testid="tool-card-cost"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <DollarSign className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">Cost Calculator</h3>
              <p className="text-[#94A3B8] mb-4 text-sm">
                Compare brand name vs compounded medication costs. See your potential savings instantly.
              </p>
              <div className="flex items-center gap-2 text-[#22C55E] font-medium text-sm group-hover:gap-3 transition-all">
                <span>Calculate Savings</span>
                <ArrowRight size={16} />
              </div>
            </Card>

            {/* Medication Match Card */}
            <Card 
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#3B82F6] transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/medication-match')}
              data-testid="tool-card-match"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Pill className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">Medication Match</h3>
              <p className="text-[#94A3B8] mb-4 text-sm">
                Answer 6 questions to find out which medication might be the better fit for your situation.
              </p>
              <div className="flex items-center gap-2 text-[#3B82F6] font-medium text-sm group-hover:gap-3 transition-all">
                <span>Find Your Match</span>
                <ArrowRight size={16} />
              </div>
            </Card>

            {/* Meal Plan Card */}
            <Card 
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#F59E0B] transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/meal-plan')}
              data-testid="tool-card-meal"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <UtensilsCrossed className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">Meal Plan Generator</h3>
              <p className="text-[#94A3B8] mb-4 text-sm">
                Get a personalized 7-day meal plan based on your medication, preferences, and challenges.
              </p>
              <div className="flex items-center gap-2 text-[#F59E0B] font-medium text-sm group-hover:gap-3 transition-all">
                <span>Create My Plan</span>
                <ArrowRight size={16} />
              </div>
            </Card>

            {/* Strength Plan Card */}
            <Card 
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#A855F7] transition-all duration-300 cursor-pointer group"
              onClick={() => navigate('/strength-plan')}
              data-testid="tool-card-strength"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#A855F7] to-[#9333EA] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Dumbbell className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">Strength Plan Builder</h3>
              <p className="text-[#94A3B8] mb-4 text-sm">
                Build a free 12-week bodyweight plan designed to help protect muscle and support consistency.
              </p>
              <div className="flex items-center gap-2 text-[#A855F7] font-medium text-sm group-hover:gap-3 transition-all">
                <span>Build My Plan</span>
                <ArrowRight size={16} />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Treatment Paths */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] text-center mb-4">
            Understanding Your Options
          </h2>
          <p className="text-[#94A3B8] text-center mb-12 max-w-2xl mx-auto">
            Different prescription medications work in different ways. Here is what you should know about each approach.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'GLP-1 Medications',
                benefits: ['Help regulate appetite', 'Support gradual progress', 'Well-studied approach']
              },
              {
                name: 'Dual-Action Options',
                benefits: ['Target multiple pathways', 'May support metabolism', 'Advanced formulations']
              },
              {
                name: 'NAD+ Therapy',
                benefits: ['Cellular energy support', 'May enhance mental clarity', 'Supports overall vitality']
              }
            ].map((treatment) => (
              <Card key={treatment.name} className="bg-[#0B1020] border-[#1E293B] p-6 hover:border-[#3B82F6] transition-all duration-300">
                <h3 className="text-2xl font-bold text-[#F8FAFC] mb-4">{treatment.name}</h3>
                <ul className="space-y-3 mb-6">
                  {treatment.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[#94A3B8]">
                      <CheckCircle2 className="text-[#3B82F6] mt-0.5 flex-shrink-0" size={20} />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <div className="mt-10 p-6 bg-[#0B1020] border border-[#1E293B] rounded-lg text-center">
            <p className="text-[#94A3B8] text-sm">
              <strong className="text-[#F8FAFC]">Not sure which is best for you?</strong> That is completely normal. During your consultation, we will discuss your goals, health history, and preferences to recommend the most appropriate option based on a provider evaluation.
            </p>
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section id="lead-form" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-4 py-2 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-full mb-4">
              <p className="text-[#22C55E] text-sm font-semibold">Free Consultation</p>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              See What Options May Be Right for You
            </h2>
            <p className="text-[#94A3B8]">
              Take the first step. Share a few details and receive personalized guidance on which medical weight loss approach may align with your goals.
            </p>
          </div>
          
          <Card className="bg-[#121A2B] border-[#1E293B] p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
                  <p className="text-red-400 text-sm">
                    {typeof error === 'string' ? error : 'Please check your information and try again.'}
                  </p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-[#F8FAFC]">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#F8FAFC]">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#F8FAFC]">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#F8FAFC]">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6] ${
                    phoneError ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  maxLength={14}
                />
                {phoneError && (
                  <p className="text-red-400 text-sm mt-1">{phoneError}</p>
                )}
              </div>

              <div>
                <Label htmlFor="primaryGoal" className="text-[#F8FAFC]">Primary Goal *</Label>
                <Select onValueChange={(value) => handleSelectChange('primaryGoal', value)} required>
                  <SelectTrigger className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC]">
                    <SelectValue placeholder="Select your primary goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weight-loss">Weight Loss</SelectItem>
                    <SelectItem value="energy">Energy</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="not-sure">Not Sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="interestedIn" className="text-[#F8FAFC]">Interested In *</Label>
                <Select onValueChange={(value) => handleSelectChange('interestedIn', value)} required>
                  <SelectTrigger className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC]">
                    <SelectValue placeholder="Select treatment option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semaglutide">Semaglutide</SelectItem>
                    <SelectItem value="tirzepatide">Tirzepatide</SelectItem>
                    <SelectItem value="nad">NAD+</SelectItem>
                    <SelectItem value="help-decide">Help Me Decide</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes" className="text-[#F8FAFC]">Optional Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Any questions or additional information..."
                  className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] focus:border-[#3B82F6]"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg py-6 h-auto"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Consultation'}
              </Button>

              <div className="mt-6 p-4 bg-[#0B1020] border border-[#1E293B] rounded-lg">
                <p className="text-sm text-center text-[#94A3B8] mb-2">
                  <strong className="text-[#F8FAFC]">What happens next?</strong>
                </p>
                <p className="text-sm text-center text-[#94A3B8]">
                  Bryan will personally review your information and reach out within 24 hours to discuss your options, answer questions, and help you take the next step.
                </p>
              </div>
            </form>
          </Card>
        </div>
      </section>

      {/* About Bryan Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-6 text-center">
            Who Is Bryan Maldonado?
          </h2>
          <div className="text-lg text-[#94A3B8] mb-8 text-center max-w-3xl mx-auto space-y-4">
            <p>
              Bryan works with DirectMeds helping people navigate GLP-1 programs like semaglutide and tirzepatide in a way that actually fits their life — not just what shows up on an insurance plan.
            </p>
            <p>
              After talking with thousands of people, one thing became clear: most people don't need more hype — they need clarity.
            </p>
            <p>
              This site was built to give you a real picture of what to expect — results, cost, side effects — before you ever have to talk to anyone.
            </p>
            <p>
              If you want to understand what this could look like for you specifically, the best place to start is your 90-day forecast.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/blog')}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-lg px-8 py-6 h-auto"
            >
              <BookOpen className="mr-2" size={20} />
              Start Reading
            </Button>
            <Button 
              onClick={() => navigate('/calculator')}
              variant="outline"
              className="border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] text-lg px-8 py-6 h-auto"
            >
              <Calculator className="mr-2" size={20} />
              See Your Forecast
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              Common Questions About Medical Weight Loss
            </h2>
            <p className="text-[#94A3B8]">
              Everything you need to know about how the process works.
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                How does medical weight loss work?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                Medical weight loss programs use prescription medications combined with lifestyle guidance to support your goals. These medications work by helping regulate appetite, metabolism, and how your body processes food. A licensed provider evaluates your health history and recommends a personalized approach tailored to your needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                Who is medical weight loss for?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                These programs may be appropriate for adults who have struggled with traditional diet and exercise approaches, those who have regained weight after previous attempts, or individuals looking for additional support with professional oversight. A provider will review your health history to determine if it is a good fit.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                What should I expect during the process?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                After your initial consultation, if approved, you will receive a personalized plan that may include prescription medication, dosing guidance, and ongoing check-ins. Most people start seeing changes within the first few weeks. You will have continuous access to support for questions, adjustments, and progress tracking.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                Are these medications safe?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                The medications used in medical weight loss programs are prescribed by licensed providers and backed by extensive clinical research. Like all medications, they can have side effects, which your provider will discuss with you. Professional oversight ensures safety and allows for adjustments based on how you respond.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                How fast will I hear back after submitting?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                Most submissions receive a response within 24 hours, often sooner. Bryan personally reviews every inquiry and reaches out to discuss your specific situation, answer questions, and outline next steps.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                Do I need to know which option I want?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                Not at all. Many people aren't sure where to start, and that's perfectly normal. The consultation process is designed to help you understand your options so you can make an informed decision with professional guidance.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-[#121A2B] border-[#1E293B] rounded-lg px-6">
              <AccordionTrigger className="text-[#F8FAFC] hover:text-[#3B82F6] text-left">
                Is pricing transparent?
              </AccordionTrigger>
              <AccordionContent className="text-[#94A3B8]">
                Yes. You'll receive clear pricing information during your consultation with no hidden fees or unexpected costs. Everything is explained upfront so you can make the best decision for your situation.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Additional Wellness Options */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              Additional Wellness Options
            </h2>
            <p className="text-[#94A3B8] max-w-3xl mx-auto text-lg">
              Many individuals exploring medical weight loss are also interested in broader wellness and recovery support. Understanding your full range of options can help you make informed decisions about your health journey.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="bg-[#0B1020] border-[#1E293B] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="text-[#3B82F6]" size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#F8FAFC] mb-3">Sermorelin Therapy</h3>
                  <p className="text-[#94A3B8] mb-4">
                    Sermorelin is a peptide therapy that may support natural processes related to energy, recovery, and metabolic function. Some individuals exploring weight management also consider peptide therapy as part of a comprehensive wellness approach.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="text-[#3B82F6] mt-1 flex-shrink-0" size={18} />
                      <span className="text-[#94A3B8] text-sm">May support energy levels and vitality</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="text-[#3B82F6] mt-1 flex-shrink-0" size={18} />
                      <span className="text-[#94A3B8] text-sm">Could assist with recovery and rest quality</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="text-[#3B82F6] mt-1 flex-shrink-0" size={18} />
                      <span className="text-[#94A3B8] text-sm">May help support metabolic function</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="text-[#3B82F6] mt-1 flex-shrink-0" size={18} />
                      <span className="text-[#94A3B8] text-sm">Requires medical evaluation and oversight</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#121A2B] border border-[#1E293B] rounded-lg p-6">
                <p className="text-[#94A3B8] text-sm mb-3">
                  <strong className="text-[#F8FAFC]">Important:</strong> All treatment options, including Sermorelin and other peptide therapies, require thorough provider evaluation. These approaches may not be appropriate for everyone. Your health history, current medications, and individual health goals will be carefully reviewed to determine if these options align with your needs.
                </p>
                <p className="text-[#94A3B8] text-sm">
                  During your consultation, we will discuss which wellness options may be most suitable for your specific situation and help you understand the potential benefits, considerations, and what to expect from each approach.
                </p>
              </div>

              <div className="mt-6 text-center">
                <Button 
                  onClick={scrollToForm}
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-lg px-8 py-6 h-auto"
                >
                  Discuss Your Wellness Options
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Wellness Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B1020]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
              Additional Wellness Support
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Explore other areas where medical support and optimization may help you feel your best.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card 
              onClick={() => navigate('/mens-hormone-support')}
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#F59E0B] transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#F8FAFC]">Men's Hormone Support</h3>
                <span className="text-xs px-2 py-1 bg-[#F59E0B]/20 text-[#F59E0B] rounded-full">Soon</span>
              </div>
              <p className="text-[#94A3B8] text-sm mb-4">Hormone optimization and vitality support for men.</p>
              <p className="text-[#F59E0B] text-sm font-semibold">Learn More →</p>
            </Card>
            
            <Card 
              onClick={() => navigate('/directmax')}
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#22C55E] transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#F8FAFC]">DirectMax</h3>
                <span className="text-xs px-2 py-1 bg-[#22C55E]/20 text-[#22C55E] rounded-full">Live</span>
              </div>
              <p className="text-[#94A3B8] text-sm mb-4">Premium men's wellness and performance support.</p>
              <p className="text-[#22C55E] text-sm font-semibold">Get Started →</p>
            </Card>

            <Card 
              onClick={() => navigate('/energy-metabolism')}
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#3B82F6] transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#F8FAFC]">Energy & Metabolism</h3>
                <span className="text-xs px-2 py-1 bg-[#F59E0B]/20 text-[#F59E0B] rounded-full">Soon</span>
              </div>
              <p className="text-[#94A3B8] text-sm mb-4">
                Resources for sustained energy, metabolic health, and lifestyle optimization.
              </p>
              <p className="text-[#3B82F6] text-sm font-semibold">Learn More →</p>
            </Card>

            <Card 
              onClick={() => navigate('/hair-health')}
              className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#3B82F6] transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[#F8FAFC]">Hair Health</h3>
                <span className="text-xs px-2 py-1 bg-[#F59E0B]/20 text-[#F59E0B] rounded-full">Soon</span>
              </div>
              <p className="text-[#94A3B8] text-sm mb-4">Solutions and support for hair thinning and promoting healthy hair growth.</p>
              <p className="text-[#3B82F6] text-sm font-semibold">Learn More →</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#3B82F6]/10 to-[#22C55E]/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-[#F8FAFC] mb-6">
            Ready to Take the First Step?
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            Get personalized guidance on which medical weight loss option may be right for you. No pressure, just clear answers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToForm}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-lg px-8 py-6 h-auto"
            >
              Get Your Free Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              onClick={() => window.location.href = 'tel:801-651-8363'}
              variant="outline"
              className="border-[#1E293B] bg-[#121A2B] hover:bg-[#1E293B] text-[#F8FAFC] text-lg px-8 py-6 h-auto"
            >
              <Phone className="mr-2" size={20} />
              Call Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#94A3B8] mb-4">© 2024 Bryan Maldonado. All rights reserved.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:801-651-8363" className="text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              801-651-8363
            </a>
            <span className="text-[#94A3B8] hidden sm:block">|</span>
            <a href="mailto:Bryan@DirectMeds.com" className="text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              Bryan@DirectMeds.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
